import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Line, LineChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useItem, useList, useAction } from '../api/queries'
import type { AssetTag, Equipment, EquipmentRow, Finding, InspectionList, Measurement, Plant, PlantNode } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { DataTable, type Column } from '../components/DataTable'
import { Icon } from '../components/Icon'
import { BandBadge, Card, ErrorState, LoadingState, PageHeader, PillFilter, RouteCriticalBadge, SearchBox, Segmented, StalenessBanner, Tabs } from '../components/ui'
import { EditEquipmentModal } from '../features/assets/EditEquipmentModal'
import { EquipmentDocuments } from '../features/assets/EquipmentDocuments'
import { EquipmentManuals } from '../features/assets/EquipmentManuals'
import { EquipmentExplodedViews } from '../features/assets/EquipmentExplodedViews'
import { SPEC_SECTIONS, TRANSMISSIONS, TRANSMISSION_KINDS, normaliseDetails } from '../features/assets/equipmentForm'
import { NewEquipmentModal } from '../features/assets/NewEquipmentModal'
import { NfcWritePanel } from '../features/nfc/NfcWritePanel'
import { LubricationPanel } from '../features/lubrication/LubricationPanel'
import { FindingsTable, InspectionsTable, MeasurementsTable } from '../features/tables/tables'
import { areaLabel } from '../lib/areas'
import { BAND_ORDER, bandMeta } from '../lib/bands'
import { formatDate } from '../lib/format'
import { useDebounced } from '../lib/hooks'
import { can } from '../lib/permissions'
import { useUser } from '../auth/AuthContext'

function Tree({ onOpen, plantId }: { onOpen: (id: number) => void; plantId?: number }) {
  const tree = useItem<PlantNode[]>('/api/assets/tree/', { plant: plantId })
  if (tree.isPending) return <LoadingState label="Cargando el árbol de activos…" />
  if (tree.isError) return <ErrorState error={tree.error} onRetry={() => void tree.refetch()} />
  return (
    <ul role="tree" aria-label="Planta, áreas y equipos" className="tree">
      {tree.data.map((plant) => (
        <li key={plant.id} role="treeitem" aria-expanded="true">
          <strong>{plant.name}</strong>
          <ul role="group">
            {plant.areas.map((area) => (
              <li key={area.id} role="treeitem" aria-expanded="true">
                <span>{areaLabel(area.code, area.name)} <span className="muted">({area.equipment.length})</span></span>
                <ul role="group">
                  {area.equipment.map((e) => (
                    <li key={e.id} role="treeitem" aria-selected="false"><button type="button" className="tree__leaf" onClick={() => onOpen(e.id)}><span className="mono">{e.code}</span> {e.name}{e.is_route_critical ? <Icon name="flag" size={12} /> : null}</button></li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

const EQUIPMENT_SORT_FIELDS = ['code', 'name', 'motor_brand', 'area', 'total'] as const
type EquipmentSortField = typeof EQUIPMENT_SORT_FIELDS[number]

export function sortEquipmentRows(rows: EquipmentRow[], ordering: string): EquipmentRow[] {
  const descending = ordering.startsWith('-')
  const requested = ordering.replace(/^-/, '')
  const field: EquipmentSortField = EQUIPMENT_SORT_FIELDS.includes(requested as EquipmentSortField)
    ? requested as EquipmentSortField
    : 'code'
  return [...rows].sort((a, b) => {
    if (field === 'total') {
      if (a.total === null && b.total !== null) return 1
      if (a.total !== null && b.total === null) return -1
      if (a.total !== null && b.total !== null) {
        const score = Number(a.total) - Number(b.total)
        if (score !== 0) return descending ? -score : score
      }
    } else {
      const value = field === 'area' ? 'area_name' : field
      const compared = String(a[value] ?? '').trim().localeCompare(String(b[value] ?? '').trim(), 'es', { numeric: true, sensitivity: 'base' })
      if (compared !== 0) return descending ? -compared : compared
    }
    return a.code.localeCompare(b.code, 'es', { numeric: true, sensitivity: 'base' })
  })
}

export function AssetsPage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const band = params.get('band') ?? ''
  const area = params.get('area') ?? ''
  const plantParam = params.get('plant') ?? ''
  const plantId = /^\d+$/.test(plantParam) ? Number(plantParam) : undefined
  const dateFrom = params.get('from') || undefined
  const dateTo = params.get('to') || undefined
  const ordering = params.get('ordering') || 'code'
  const route = params.get('route') === '1'
  const [search, setSearch] = useState('')
  const [view, setView] = useState<'list' | 'tree'>('list')
  const [creating, setCreating] = useState(false)
  const user = useUser()
  const q = useDebounced(search)
  const setParam = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n, { replace: true }) }
  const plant = useItem<Plant>(plantId ? `/api/plants/${plantId}/` : null)
  const areas = useList<{ id: number; code: string; name: string }>('/api/areas/', { page_size: 50, plant: plantId })
  const rows = useItem<EquipmentRow[]>('/api/equipment-status/', { plant: plantId, date_from: dateFrom, date_to: dateTo, band, area, is_route_critical: route ? 'true' : undefined, search: q })
  const sortedRows = useMemo(() => sortEquipmentRows(rows.data ?? [], ordering), [rows.data, ordering])
  const latest = rows.data?.map((r) => r.inspected_on).filter((d): d is string => Boolean(d)).sort().at(-1)
  const columns: Column<EquipmentRow>[] = [
    { key: 'code', header: 'Código', sortKey: 'code', cell: (r) => <span className="mono">{r.code}</span> },
    { key: 'name', header: 'Equipo', sortKey: 'name', cell: (r) => <>{r.name} {r.is_route_critical ? <RouteCriticalBadge /> : null}</> },
    { key: 'motor_brand', header: 'Marca del motor', sortKey: 'motor_brand', cell: (r) => <span className="equipment-brand">{r.motor_brand?.trim() || <span className="muted">Sin registrar</span>}</span> },
    { key: 'area', header: 'Área', sortKey: 'area', cell: (r) => areaLabel(undefined, r.area_name), priority: 'low' },
    { key: 'score', header: 'Puntaje', sortKey: 'total', cell: (r) => <BandBadge band={r.band} total={r.total} size="sm" />, align: 'right' },
    { key: 'date', header: 'Inspeccionado', cell: (r) => formatDate(r.inspected_on), priority: 'low' },
  ]
  return (
    <div className="page">
      <PageHeader title={plantId ? `Equipos de ${plant.data?.name ?? 'la planta'}` : 'Activos'} subtitle={plantId ? 'Listado limitado a esta planta' : 'Estado actual de cada equipo'} actions={<>{can(user.role, 'equipment.create') ? <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>Nuevo equipo</button> : null}<Segmented small label="Vista" value={view} onChange={setView} options={[{ value: 'list', label: 'Lista' }, { value: 'tree', label: 'Árbol' }]} /></>} />
      {latest ? <StalenessBanner inspectedOn={latest} /> : null}
      <Card>
        <div className="toolbar">
          <SearchBox label="Buscar equipos" value={search} onChange={setSearch} placeholder="Buscar por código o nombre" />
          <label className="select-pill"><span className="sr-only">Área</span>
            <select aria-label="Área" value={area} onChange={(e) => setParam('area', e.target.value)}>
              <option value="">Todas las áreas</option>
              {areas.data?.results.map((a) => <option key={a.id} value={a.id}>{areaLabel(a.code, a.name)}</option>)}
            </select>
          </label>
          <PillFilter active={route} onClick={() => setParam('route', route ? '' : '1')}>Ruta crítica</PillFilter>
        </div>
        <div className="pills" role="group" aria-label="Banda">
          <PillFilter active={band === ''} onClick={() => setParam('band', '')}>Todos</PillFilter>
          {BAND_ORDER.map((b) => <PillFilter key={b} active={band === b} onClick={() => setParam('band', b)}>{bandMeta(b).label}</PillFilter>)}
        </div>
        {view === 'tree' ? <Tree plantId={plantId} onOpen={(id) => navigate(`/assets/${id}`)} /> : (
          <DataTable caption="Equipos" columns={columns} rows={rows.data ? sortedRows : undefined} loading={rows.isPending} error={rows.error} onRetry={() => void rows.refetch()} ordering={ordering} onOrdering={(value) => setParam('ordering', value)} rowKey={(r) => r.equipment} onRowClick={(r) => navigate(`/assets/${r.equipment}`)} rowLabel={(r) => `Abrir ${r.name}`} emptyTitle="Ningún equipo coincide" emptyHint="Cambiar o quitar los filtros." />
        )}
      </Card>
      {creating ? <NewEquipmentModal onClose={() => setCreating(false)} /> : null}
    </div>
  )
}

const SPEC_GROUPS: { title: string; fields: [keyof Equipment, string, [string, string][] | undefined][] }[] = [
  ...SPEC_SECTIONS.map((s) => ({ title: s.title, fields: s.fields.map((x): [keyof Equipment, string, [string, string][] | undefined] => [x.key as keyof Equipment, x.label, x.options]) })),
  { title: 'Transmisión', fields: [['transmission_type', 'Tipo', TRANSMISSIONS]] },
]

type Details = Record<string, unknown>

const Missing = () => <span className="faint">Sin registrar</span>

function SpecTable({ title, columns, rows }: { title: string; columns: [string, string][]; rows: Record<string, unknown>[] }) {
  return (
    <Card title={title}>
      {rows.length === 0 ? <p className="muted">Sin registrar</p> : (
        <table className="table">
          <caption className="sr-only">{title}</caption>
          <thead><tr>{columns.map(([, label]) => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{rows.map((r, n) => <tr key={n}>{columns.map(([k]) => <td key={k}>{r[k] === null || r[k] === undefined || r[k] === '' ? '—' : String(r[k])}</td>)}</tr>)}</tbody>
        </table>
      )}
    </Card>
  )
}

function Specs({ eq }: { eq: Equipment }) {
  const details = normaliseDetails(eq.transmission_details) as Partial<Record<'chain' | 'belt' | 'coupling', Details>>
  const kinds = TRANSMISSION_KINDS.filter((k) => k.fields.some(([f]) => details[k.key]?.[f]))
  const shown = (v: unknown, options?: [string, string][]) => (options?.find(([value]) => value === v)?.[1] ?? v)
  return (
    <div className="stack">
      <div className="grid grid--3">
        {SPEC_GROUPS.map((g) => (
          <Card key={g.title} title={g.title}>
            <dl className="defs defs--specs">
              {g.fields.map(([k, label, options]) => { const v = shown(eq[k], options); return <div key={k}><dt>{label}</dt><dd>{v === null || v === undefined || v === '' ? <Missing /> : String(v)}</dd></div> })}
            </dl>
          </Card>
        ))}
      </div>
      {kinds.length ? (
        <div className="grid grid--3">
          {kinds.map((k) => (
            <Card key={k.key} title={k.title}>
              <dl className="defs defs--specs">{k.fields.map(([f, label]) => { const v = details[k.key]?.[f]; return <div key={f}><dt>{label}</dt><dd>{v ? String(v) : <Missing />}</dd></div> })}</dl>
            </Card>
          ))}
        </div>
      ) : null}
      <div className="grid grid--3">
        <SpecTable title="Rodamientos" columns={[['reference', 'Referencia'], ['fixing_sleeve', 'Manguito de fijación']]} rows={eq.bearings ?? []} />
        <SpecTable title="Retenedores" columns={[['reference', 'Referencia']]} rows={eq.retainers ?? []} />
        <SpecTable title="Chumaceras" columns={[['reference', 'Referencia']]} rows={eq.pillow_blocks ?? []} />
      </div>
      <SpecTable title="Lista de partes" columns={[['position', 'Pos.'], ['description', 'Descripción'], ['reference', 'Referencia'], ['quantity', 'Cant.'], ['sheet', 'Plano']]} rows={eq.parts_list ?? []} />
      <Card title="Observaciones">{eq.notes ? <p className="pre-line">{eq.notes}</p> : <p className="muted">Sin registrar</p>}</Card>
    </div>
  )
}

function ScoreHistory({ rows }: { rows: InspectionList[] }) {
  const data = [...rows].filter((r) => r.total !== null && r.date_flag !== 'date_suspect').sort((a, b) => a.inspected_on.localeCompare(b.inspected_on)).map((r) => ({ label: formatDate(r.inspected_on), total: Number(r.total) }))
  if (data.length === 0) return <p className="muted">No hay inspecciones con puntaje para graficar.</p>
  return (
    <div className="chart" style={{ height: 220 }}>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 480, height: 220 }}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: -16 }}>
          <CartesianGrid vertical={false} stroke="var(--line)" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} padding={{ left: 20, right: 20 }} />
          <YAxis domain={[0, 4]} tick={{ fontSize: 12 }} />
          <ReferenceLine y={2.5} stroke="var(--band-critical)" strokeDasharray="5 4" />
          <Tooltip />
          <Line dataKey="total" stroke="var(--brand)" strokeWidth={2.5} dot={{ r: 5, fill: '#fff' }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function TagPanel({ eq }: { eq: Equipment }) {
  const user = useUser()
  const staff = can(user.role, 'tags.manage')
  const tags = useItem<AssetTag[]>(staff ? `/api/equipment/${eq.id}/tags/` : null)
  const register = useAction<{ kind: 'nfc' | 'qr' }, AssetTag>('post', `/api/equipment/${eq.id}/tags/`, [`/api/equipment/${eq.id}/tags/`])
  const revoke = useAction<number, AssetTag>('post', (id) => `/api/asset-tags/${id}/revoke/`, [`/api/equipment/${eq.id}/tags/`])
  const url = `${window.location.origin}/e/${eq.code}`
  return (
    <div className="grid grid--2">
      <Card title="QR imprimible" actions={<button type="button" className="btn btn--ghost btn--sm" onClick={() => window.print()}><Icon name="printer" size={14} /> Imprimir</button>}>
        <div className="qr-print">
          <img src={`/api/equipment/${eq.id}/qr/`} alt={`Código QR de ${eq.code}`} width={220} height={220} />
          <p className="mono">{eq.code}</p><p className="muted">{eq.name}</p>
        </div>
      </Card>
      <Card title="URL de la etiqueta">
        <p>Escribir esta dirección en una etiqueta NFC (registro NDEF URI) o imprimirla como QR. Cualquier teléfono la abre sin necesidad de una aplicación.</p>
        <p className="mono url-box">{url}</p>
        <p className="faint small">Una etiqueta es un localizador, no una prueba de presencia: las etiquetas se pueden clonar.</p>
        {staff ? (
          <>
            <h3>Etiquetas registradas</h3>
            {tags.data?.length ? <ul className="plain">{tags.data.map((t) => <li key={t.id}>{t.kind.toUpperCase()} {t.uid ?? ''} {t.active ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => revoke.mutate(t.id)}>Revocar</button> : <span className="faint">revocada</span>}</li>)}</ul> : <p className="muted">No hay etiquetas registradas.</p>}
            <div className="row"><button type="button" className="btn btn--primary btn--sm" onClick={() => register.mutate({ kind: 'nfc' })}>Registrar etiqueta (NFC)</button><button type="button" className="btn btn--ghost btn--sm" onClick={() => register.mutate({ kind: 'qr' })}>Registrar etiqueta (QR)</button></div>
          </>
        ) : null}
      </Card>
      {staff ? <NfcWritePanel tags={Array.isArray(tags.data) ? tags.data : []} /> : null}
    </div>
  )
}

type Tab = 'overview' | 'inspections' | 'findings' | 'measurements' | 'lubrication' | 'tags' | 'manuals' | 'exploded'

const TABS: Tab[] = ['overview', 'inspections', 'findings', 'measurements', 'lubrication', 'tags', 'manuals', 'exploded']

export function AssetDetailPage() {
  const { id } = useParams()
  const { user } = useAuth()
  const [params] = useSearchParams()
  const requested = params.get('tab') as Tab | null  // ?tab=inspections opens the score history (dashboard drill-down)
  const [tab, setTab] = useState<Tab>(requested && TABS.includes(requested) ? requested : 'overview')
  const [editing, setEditing] = useState(false)
  const eq = useItem<Equipment>(`/api/equipment/${id}/`)
  const hist = useList<InspectionList>(`/api/equipment/${id}/history/`, { page_size: 100 })
  const findings = useList<Finding>('/api/findings/', { equipment: id }, { enabled: tab === 'findings' })
  const meas = useList<Measurement>('/api/measurements/', { equipment: id }, { enabled: tab === 'measurements' })
  if (eq.isPending) return <LoadingState label="Cargando equipo…" />
  if (eq.isError) return <ErrorState error={eq.error} onRetry={() => void eq.refetch()} />
  const e = eq.data
  const current = hist.data?.results.find((r) => r.is_current) ?? hist.data?.results[0]
  const writer = user && can(user.role, 'inspection.create')
  const editor = user && can(user.role, 'equipment.edit')
  return (
    <div className="page">
      <PageHeader title={e.name} subtitle={<><span className="mono">{e.code}</span> · {areaLabel(undefined, e.area_name)} {e.is_route_critical ? <RouteCriticalBadge /> : null}</>}
        actions={<>{current ? <BandBadge band={current.band} total={current.total} size="lg" /> : <BandBadge band="not_assessed" size="lg" />}{editor ? <button type="button" className="btn btn--ghost" onClick={() => setEditing(true)}>Editar equipo</button> : null}{writer ? <Link className="btn btn--primary" to={`/e/${e.code}`}>Acciones de campo</Link> : null}</>} />
      {current ? <StalenessBanner inspectedOn={current.inspected_on} /> : null}
      <Tabs label="Secciones del equipo" value={tab} onChange={setTab} tabs={[{ value: 'overview', label: 'Resumen' }, { value: 'inspections', label: 'Inspecciones' }, { value: 'findings', label: 'Hallazgos' }, { value: 'measurements', label: 'Mediciones' }, { value: 'lubrication', label: 'Lubricación' }, { value: 'tags', label: 'NFC / QR' }, { value: 'manuals', label: 'Manual' }, { value: 'exploded', label: 'Despiece' }]} />
      {tab === 'overview' ? <><EquipmentDocuments equipmentId={e.id} /><Specs eq={e} />{e.is_route_critical ? <p className="muted">Origen de ruta crítica: {e.route_critical_source}</p> : null}</> : null}
      {tab === 'inspections' ? <div className="stack"><Card title="Historial de puntaje"><ScoreHistory rows={hist.data?.results ?? []} /></Card><InspectionsTable rows={hist.data?.results} showEquipment={false} loading={hist.isPending} error={hist.error} /></div> : null}
      {tab === 'findings' ? <FindingsTable rows={findings.data?.results} showEquipment={false} loading={findings.isPending} error={findings.error} /> : null}
      {tab === 'measurements' ? <MeasurementsTable rows={meas.data?.results} showEquipment={false} loading={meas.isPending} error={meas.error} /> : null}
      {tab === 'lubrication' ? <LubricationPanel equipmentId={e.id} /> : null}
      {tab === 'tags' ? <TagPanel eq={e} /> : null}
      {tab === 'manuals' ? <EquipmentManuals equipmentId={e.id} /> : null}
      {tab === 'exploded' ? <EquipmentExplodedViews equipmentId={e.id} /> : null}
      {editing ? <EditEquipmentModal equipment={e} onClose={() => setEditing(false)} /> : null}
    </div>
  )
}
